import ConfirmationModal from "@/components/ConfirmationModal";
import { useEncounterProcedures } from "@/encounters/contexts/EncounterProceduresContext";
import { ProceduresEditTarget } from "@/encounters/types/procedures";
import { buildDiagnosisRemovalOperation } from "@/encounters/utils/staged-procedures";
import { getCodeableConceptCode } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

interface DiagnosisRemovalModalProps {
  /** The diagnosis being removed, with the procedures recorded under it. `null` keeps the modal closed. */
  target: ProceduresEditTarget | null;
  onClose: () => void;
}

/**
 * The confirmation for removing one of the encounter's diagnoses, and the removal itself.
 *
 * Every section that lists the encounter's diagnoses removes one through this, so the copy a
 * provider reads and the operation that gets staged are the same wherever they clicked Delete. It
 * stages onto the encounter's own save through `EncounterProceduresProvider`, so a removal confirmed
 * on one tab is previewed on all of them.
 */
function DiagnosisRemovalModal({ target, onClose }: DiagnosisRemovalModalProps) {
  const { stageOperation } = useEncounterProcedures();

  // On save the diagnosis and the procedures left pointing at nothing else are retired, not
  // deleted, so the record survives.
  const handleConfirm = () => {
    if (!target) return;

    stageOperation(buildDiagnosisRemovalOperation(target.condition, target.procedures));
    onClose();
    toast({
      title: "Diagnosis removed",
      message: "The diagnosis will be removed when you save the encounter",
      type: "success",
      autoClose: 3000,
    });
  };

  const diagnosisCode = target ? getCodeableConceptCode(target.condition.code) : "";

  return (
    <ConfirmationModal
      onClose={onClose}
      isDeleting={false}
      opened={target !== null}
      onConfirm={handleConfirm}
      title="Remove this diagnosis?"
      confirmButtonText="Yes, Remove"
      modalTestId="diagnosis-removal-modal"
      cancelButtonTestId="diagnosis-removal-cancel-button"
      confirmButtonTestId="diagnosis-removal-confirm-button"
      description={`Removing ${diagnosisCode} also removes the procedures recorded under it, unless they are also linked to another diagnosis. Nothing is deleted, so it all stays in the record, and it leaves the encounter when you save.`}
    />
  );
}

export default DiagnosisRemovalModal;
